import type {
    Answer,
    Card,
    DynamicCoreQuestion,
    PollResultCard,
    Question,
    ThankYouCard,
    TitleCard,
} from '@dmp/qqms/types';

/**
 * Returns true if the given card is a terminal card type.
 * Terminal cards are the only cards that should end a survey flow.
 */
export function isTerminalCard(
    card: Card
): card is ThankYouCard | PollResultCard {
    return ['thankYouCard', 'pollResultCard'].includes(card.type);
}

interface SelectNextCardIdPayload {
    card: Question | DynamicCoreQuestion | TitleCard;
    allCards: Card[];
    // The ID of the selected answer. Required for 'forkedQuestion' to determine the next card.
    answerId?: Answer['id'];
}

/**
 * Determine the next Card to show based on what Answer was chosen.
 */
export function selectNextCardId({
    answerId,
    card,
    allCards,
}: SelectNextCardIdPayload): Card['id'] | undefined {
    // find next index card to fallback for undefined `resultCard`
    const idx = allCards.findIndex((c) => c.id === card.id);
    const nextIndexCard = allCards[idx + 1];
    const nextIndexCardId = nextIndexCard?.id || undefined;

    switch (card.type) {
        case 'linearQuestion':
        case 'dynamicCoreQuestion':
        case 'titleCard':
            return card.resultCard || nextIndexCardId;

        case 'forkedQuestion': {
            if (answerId === undefined) {
                return undefined;
            }

            const answer = card.answers.find((o) => o.id === answerId);

            if (answer === undefined) {
                throw new Error(`Unable to find Answer ${answerId}.`);
            }

            return answer.resultCard || nextIndexCardId;
        }

        default:
            card satisfies never;
            throw new Error('Unsupported Card type');
    }
}

/**
 * Determine if the given expiration date has passed.
 */
export function isPollResultExpirationPast(expiresAt?: string): boolean {
    return expiresAt !== undefined && new Date(expiresAt) <= new Date();
}
